<template>
  <li
    class="nav-item softphone-header"
    data-cy="softphone-header"
  >
    <!--
      The collapsed softphone. A plain button rather than a b-nav-item so the expanded state can be
      announced properly - this is a disclosure, not a link.
    -->
    <button
      ref="toggle"
      type="button"
      class="btn btn-link btn-icon bg-transparent softphone-header__toggle d-flex flex-column align-items-center"
      :class="{
        'softphone-header__toggle--ringing': isRinging,
        'softphone-header__toggle--idle': !canOpenPanel
      }"
      :title="toggleTitle"
      :aria-disabled="String(!canOpenPanel)"
      :aria-expanded="String(isExpanded)"
      aria-controls="softphone-header-panel"
      data-cy="softphone-header-toggle"
      @click="toggle"
    >
      <font-awesome-icon
        :icon="['fa', toggleIcon]"
        class="softphone-header__icon"
      />
      <!--
        The state rather than the widget's name. An agent knows what the phone in their header is;
        what they cannot see with the panel collapsed is whether it is taking calls - and Offline,
        Ready or On call is the one thing they check it for.

        Deliberately NOT gated on owning the softphone, unlike the tab-title marker: this says what
        the AGENT's shift is doing, and they are taking calls whichever tab holds the transport. The
        marker is gated because a green glyph on three tabs at once makes the one tab that is really
        on the call impossible to pick out of the tab strip; a header is only ever read one tab at a
        time, and the panel under it names the tab that has the controls.
      -->
      <span
        class="softphone-header__label"
        data-cy="softphone-header-status"
      >{{ softphoneStatusLabel }}</span>

      <!--
        The same alert dot the Support tab uses, for the same reason: this is the notification that
        reaches an agent who has the panel collapsed and is working somewhere else on the page. It
        shows in every tab, not just the one on the call, because a call the agent cannot see is
        exactly the thing worth pointing at.
      -->
      <span
        v-if="isRinging"
        class="softphone-header__alert"
        data-cy="softphone-header-alert"
      >
        <font-awesome-icon :icon="['fa', 'phone-volume']" />
      </span>

      <span
        v-else-if="statusDotClass"
        class="softphone-header__dot"
        :class="statusDotClass"
        data-cy="softphone-header-dot"
      />
    </button>

    <div
      v-if="isExpanded"
      id="softphone-header-panel"
      ref="panel"
      class="softphone-header__panel"
      data-cy="softphone-header-panel"
    >
      <!--
        The panel closes behind Go offline. The agent has said they are done taking calls for now,
        and the panel is where they say it - leaving it open in front of them afterwards reads as
        the click not having landed. Nothing else in the widget closes it: an agent mid-call keeps
        the panel they opened.
      -->
      <SoftphoneWidget @went-offline="collapse" />
    </div>
  </li>
</template>

<script>
import { mapGetters, mapState } from 'vuex'

import SoftphoneWidget from '@/components/softphone/SoftphoneWidget.vue'
import { armRingtone, isRingtoneArmed } from '@/services/softphone/ringtone'
import { BLOCKED_STATUSES, SOFTPHONE_STATUS } from '@/store/modules/softphone.store'

/**
 * What the browser tab's title wears, as a MARKER on whatever the title happens to be rather than a
 * replacement for it, so the page's own title is never lost.
 *
 * The same states the dot on the collapsed header shows, said where an agent working in a different
 * tab can read them - the header is only visible in the tab they are looking at. A title is plain
 * text, so the colour is the emoji's own: these glyphs are the palette, not a choice.
 *
 * Only the incoming call gets words. The rest are one glyph: they are a state the agent glances at,
 * not news, and a tab strip gives a title about a dozen characters before it cuts it off.
 */
const TITLE_MARKERS = Object.freeze({
  ringing: '📞 Incoming call · ',
  // The same phone as the ringing marker, standing still and saying nothing: it is the same call,
  // and the difference between one being offered and one being on is that the tab stopped flashing.
  onCall: '📞 ',
  ready: '🟢 ',
  // Connecting and the wrap-up: taking calls, but not this second.
  waiting: '🟡 ',
  // The error that leaves an agent not taking calls without their asking. Offline gets NOTHING: it
  // is the state the app is in nearly all the time, for nearly every user, and a marker that is
  // almost always there says nothing when it is. Unicode has no grey circle - the pale one is as
  // close as a title can get, and the point of it is that it is the marker with no colour in it.
  error: '⚪ '
})

// Longest first, so the ringing marker is recognised as itself rather than as the On call marker it
// happens to begin with.
const ALL_TITLE_MARKERS = Object.freeze(
  Object.values(TITLE_MARKERS).sort((a, b) => b.length - a.length)
)

function markerOn(title) {
  return ALL_TITLE_MARKERS.find(marker => title.startsWith(marker)) || ''
}

function withoutMarker(title) {
  return title.slice(markerOn(title).length)
}

// Whatever is on the title now comes off first: the tab goes from Ready to On call without passing
// through a state that has no marker, and two glyphs stacked up would be one state too many.
function withMarker(title, marker) {
  return `${marker}${withoutMarker(title)}`
}

export default {
  name: 'SoftphoneHeaderWidget',

  components: {
    SoftphoneWidget
  },

  data() {
    return {
      isExpanded: false,
      titleFlashIntervalId: null,
      onDocumentPointerDown: null,
      onDocumentKeyDown: null,
      retryArm: null
    }
  },

  computed: {
    ...mapState('softphone', ['lastError', 'status']),

    ...mapGetters('softphone', [
      'hasSoftphoneResumePrompt',
      'isCallActive',
      'isRinging',
      'isOfflineForHiddenPage',
      'isSoftphoneBlockedFromCalls',
      'isSoftphoneOnline',
      'softphoneBlockedNotice',
      'isSoftphoneOwner',
      'softphoneStatusLabel'
    ]),

    // The panel opens itself on each of these, so they are watched as one thing - see the watcher.
    softphoneAttention() {
      return {
        isRinging: this.isRinging,
        isOnline: this.isSoftphoneOnline,
        isOwner: this.isSoftphoneOwner,
        hasResumePrompt: this.hasSoftphoneResumePrompt,
        isOfflineForHiddenPage: this.isOfflineForHiddenPage
      }
    },

    toggleIcon() {
      if (this.isRinging) {
        return 'phone-volume'
      }

      // The slashed phone, even though the shift is on and the registration is up: what this icon
      // answers is "are calls coming", and while the account cannot buy them the answer is no. An
      // unslashed phone here is the exact thing that let a capped account look like a working one.
      if (this.isSoftphoneBlockedFromCalls) {
        return 'phone-slash'
      }

      return this.isSoftphoneOnline ? 'phone' : 'phone-slash'
    },

    /**
     * Whether the panel has anything to show, which is what makes the toggle clickable.
     *
     * Dead only when the panel would be EMPTY: offline, with nothing to explain and nothing to
     * accept. Every control in it is dead there, and going live is not done from here anyway - it
     * is the Go Live button on a campaign, because the agent has to say WHICH campaign they are
     * taking calls for. A toggle that opens a panel of dead buttons reads as the phone being
     * broken rather than idle.
     *
     * Two things keep it live while the status says Offline, and both are the only way out of it:
     *
     * - An error. A lost registration takes the agent offline without their asking and leaves the
     *   reason in `lastError`, with the status still Offline (see `handleUnregistered`) - and the
     *   panel is the only place that reason is ever shown. A dead toggle would leave an agent who
     *   has quietly stopped receiving calls with nothing to read.
     * - The resume offer, in a tab that inherited the softphone with the campaign paused;
     *   accepting it is the agent's way back to taking calls.
     */
    canOpenPanel() {
      return this.status !== SOFTPHONE_STATUS.OFFLINE
        || this.hasSoftphoneResumePrompt
        || Boolean(this.lastError)
    },

    toggleTitle() {
      if (this.isRinging) {
        return 'Incoming call'
      }

      if (this.isCallActive) {
        return 'On a call'
      }

      // Said on the collapsed toggle as well as in the panel: this is the state an agent is most
      // likely to be looking at from somewhere else on the page.
      if (this.status === SOFTPHONE_STATUS.RECONNECTING) {
        return 'Reconnecting...'
      }

      // Whatever went wrong, the panel is where it says so - including the lost registration that
      // leaves the status at Offline.
      //
      // Ahead of the blocked notice below, and the two DO coincide: a failed wrap-up pause leaves
      // an error behind on a widget that is still online, and the account can be capped at the same
      // time. The cap is spelled out in the panel either way, while this tooltip is the only thing
      // that ever says there is an error in there to read.
      if (this.lastError) {
        return 'Something went wrong - open for the reason'
      }

      // Same reasoning as Reconnecting, and the label under the icon has room for two words only -
      // so the tooltip is where the collapsed header gets to say which cap it is and what it costs
      // the agent. Worded from the same source as the panel, so the two cannot drift apart.
      if (this.softphoneBlockedNotice) {
        return this.softphoneBlockedNotice.text
      }

      if (!this.canOpenPanel) {
        return 'You are not taking calls - go live from a campaign'
      }

      // Not "the call controls": the panel also carries the resume offer, the takeover offer and
      // the error, and none of those is a control for a call.
      return this.isExpanded ? 'Hide the phone' : 'Show the phone'
    },

    /**
     * What the tab's title wears while nothing is ringing: the same states as the dot on the
     * collapsed header, in the same order of precedence.
     *
     * Only in the tab that OWNS the softphone. A mirror renders the owner's state faithfully - that
     * is the point of it - but a green circle on three tabs at once says the agent is taking calls
     * in three places, and the one tab that IS taking them is then the hardest to find. The ringing
     * marker is deliberately the other way round: see `syncTitleMarker`.
     */
    steadyTitleMarker() {
      if (!this.isSoftphoneOwner) {
        return ''
      }

      if (this.isCallActive) {
        return TITLE_MARKERS.onCall
      }

      if (this.status === SOFTPHONE_STATUS.READY) {
        return TITLE_MARKERS.ready
      }

      // Reconnecting belongs with these two rather than with the error: the shift is not over, the
      // widget is getting the registration back, and a tab marked as failed would send the agent
      // looking for a problem to fix.
      const isWaiting = this.status === SOFTPHONE_STATUS.CONNECTING
        || this.status === SOFTPHONE_STATUS.WRAP_UP
        || this.status === SOFTPHONE_STATUS.RECONNECTING

      if (isWaiting) {
        return TITLE_MARKERS.waiting
      }

      // A capped account wears the error marker rather than the waiting one. Yellow would put it
      // with Connecting and the wrap-up - live but not this second, and back by itself in a moment
      // - and this is the opposite: nothing is coming until somebody acts on the account.
      const isNotTakingCalls = this.status === SOFTPHONE_STATUS.ERROR
        || BLOCKED_STATUSES.includes(this.status)

      return isNotTakingCalls ? TITLE_MARKERS.error : ''
    },

    // Everything the title reacts to, watched as one thing - see the watcher.
    titleState() {
      return {
        isRinging: this.isRinging,
        marker: this.steadyTitleMarker
      }
    },

    /**
     * A colour on the collapsed icon, so an agent who never opens the panel can still see at a
     * glance that they are taking calls. Offline shows nothing: a grey dot on a grey header reads
     * as a rendering artefact rather than a state.
     */
    statusDotClass() {
      if (this.isCallActive) {
        return 'softphone-header__dot--active'
      }

      if (this.status === SOFTPHONE_STATUS.WRAP_UP) {
        return 'softphone-header__dot--wrap-up'
      }

      if (this.status === SOFTPHONE_STATUS.READY) {
        return 'softphone-header__dot--ready'
      }

      if (this.status === SOFTPHONE_STATUS.CONNECTING || this.status === SOFTPHONE_STATUS.RECONNECTING) {
        return 'softphone-header__dot--connecting'
      }

      // Red, with the error, and for the same reason the badge in the panel is: the agent has
      // stopped receiving calls without asking to, and nothing they can do from here ends it.
      if (this.status === SOFTPHONE_STATUS.ERROR || this.isSoftphoneBlockedFromCalls) {
        return 'softphone-header__dot--error'
      }

      return ''
    }
  },

  watch: {
    /**
     * When the panel opens itself, and when the tab title starts flashing.
     *
     * Four things open it, all in the tab that OWNS the softphone and none of them in a mirror -
     * a panel springing open in three tabs at once, two of them with dead buttons, is noise:
     *
     * - A call arriving. The agent has seconds to decide, and hunting for a collapsed widget is
     *   not how they should spend them.
     * - Going live. This is the confirmation that the agent is taking calls; it replaced a success
     *   toast, which said the same thing in a place the agent had to read and then dismiss.
     * - A resume offer, after this tab inherited the softphone from one that went away. Unopened,
     *   the offer is invisible and the agent simply stops receiving calls with no idea why.
     * - Going offline because a phone hid the page. Opened while the page is still hidden, so the
     *   reason is the first thing the agent sees on coming back rather than an Offline they did not
     *   ask for, with the explanation behind the toggle.
     */
    softphoneAttention({ isRinging, isOnline, isOwner, hasResumePrompt, isOfflineForHiddenPage }, previous = {}) {
      const isNewlyRinging = isRinging && !previous.isRinging
      const isNewlyOnline = isOnline && !previous.isOnline
      const isNewlyOfflineForHiddenPage = isOfflineForHiddenPage && !previous.isOfflineForHiddenPage

      if (isOwner && (isNewlyRinging || isNewlyOnline || isNewlyOfflineForHiddenPage)) {
        this.expand()
      }

      if (hasResumePrompt && !previous.hasResumePrompt) {
        this.expand()
      }

    },

    /**
     * A panel the toggle can no longer reopen must not be left standing open - and the agent can
     * go offline from outside this widget entirely (the campaigns list has its own Go Offline, and
     * another tab taking the softphone over ends this tab's shift without a click at all).
     *
     * The widget's own Go offline already closes it - see the `went-offline` handler - so this is
     * about every other way the agent stops taking calls.
     */
    canOpenPanel(canOpen) {
      if (!canOpen) {
        this.collapse()
      }
    },

    // Every state the title says something about, including the ones the panel ignores.
    titleState() {
      this.syncTitleMarker()
    },

    /**
     * The router's own title mixin rewrites `document.title` on navigation, taking the marker with
     * it. Put back on the tick after, so the page has set its title first.
     */
    $route() {
      this.$nextTick(this.syncTitleMarker)
    }
  },

  mounted() {
    // The ringtone needs a user gesture before it can play, and a ringtone that stays silent is the
    // one failure an agent cannot see coming. Any interaction anywhere in the tab will do, and this
    // tab may not own the softphone yet when that interaction happens.
    this.armOnFirstInteraction()

    // Not just a no-op on a fresh mount: a call can already be ringing when this instance is
    // created (a route change re-creates the header), and a watcher does not fire for state that
    // was already true. It also clears a marker left on the title by an instance that was
    // destroyed mid-flash.
    this.syncTitleMarker()
  },

  beforeDestroy() {
    this.clearTitleMarker()
    this.removeDismissListeners()
    this.removeArmListeners()
  },

  methods: {
    // Aria-disabled is a promise to whoever reads the button, not a bar on the event: the refusal
    // itself is here.
    toggle() {
      if (!this.canOpenPanel) {
        return
      }

      if (this.isExpanded) {
        this.collapse()

        return
      }

      this.expand()
    },

    expand() {
      if (this.isExpanded || !this.canOpenPanel) {
        return
      }

      this.isExpanded = true

      this.addDismissListeners()
    },

    collapse() {
      if (!this.isExpanded) {
        return
      }

      this.isExpanded = false

      this.removeDismissListeners()
    },

    /**
     * Click-outside and Escape, the two ways a header panel is expected to close. Deliberately not
     * closed by anything the softphone itself does: an agent mid-call keeps the panel they opened.
     */
    addDismissListeners() {
      if (this.onDocumentPointerDown) {
        return
      }

      this.onDocumentPointerDown = event => {
        const isInside = this.$el.contains(event.target)

        if (!isInside) {
          this.collapse()
        }
      }

      this.onDocumentKeyDown = event => {
        if (event.key !== 'Escape') {
          return
        }

        this.collapse()

        if (this.$refs.toggle) {
          this.$refs.toggle.focus()
        }
      }

      document.addEventListener('pointerdown', this.onDocumentPointerDown)
      document.addEventListener('keydown', this.onDocumentKeyDown)
    },

    removeDismissListeners() {
      if (this.onDocumentPointerDown) {
        document.removeEventListener('pointerdown', this.onDocumentPointerDown)
        this.onDocumentPointerDown = null
      }

      if (this.onDocumentKeyDown) {
        document.removeEventListener('keydown', this.onDocumentKeyDown)
        this.onDocumentKeyDown = null
      }
    },

    armOnFirstInteraction() {
      if (isRingtoneArmed()) {
        return
      }

      this.retryArm = async () => {
        await armRingtone()

        if (isRingtoneArmed()) {
          this.removeArmListeners()
        }
      }

      window.addEventListener('pointerdown', this.retryArm)
      window.addEventListener('keydown', this.retryArm)
    },

    removeArmListeners() {
      if (!this.retryArm) {
        return
      }

      window.removeEventListener('pointerdown', this.retryArm)
      window.removeEventListener('keydown', this.retryArm)

      this.retryArm = null
    },

    /**
     * What the tab's title says, decided from scratch each time rather than toggled - which is what
     * lets it be called from a watcher, from a navigation and from `mounted` without any of them
     * needing to know what the others did.
     *
     * A ringing call is the one thing that FLASHES, and it flashes in EVERY tab, whether or not the
     * agent is looking at it and whether or not that tab owns the softphone. A call the agent has
     * not answered yet is the one state worth interrupting them over, and the flashing title is
     * what says so at a glance - so it keeps saying it until the call is answered or declined
     * rather than stopping the moment the tab comes into focus. Which tab holds the transport is
     * an implementation detail; the call has to reach the agent in all of them.
     *
     * Everything else is a steady glyph, and a state rather than news: the tab may as well carry it
     * while the agent is looking at it.
     */
    syncTitleMarker() {
      if (this.isRinging) {
        this.startTitleFlash()

        return
      }

      this.stopTitleFlash()

      document.title = withMarker(document.title, this.steadyTitleMarker)
    },

    // The title as the page itself wrote it. For a tab that is going away, or one that has nothing
    // left to say about the softphone.
    clearTitleMarker() {
      this.stopTitleFlash()

      document.title = withoutMarker(document.title)
    },

    /**
     * The marker is applied to, and stripped from, whatever `document.title` IS at the time - not
     * restored from a title captured when the flash began.
     *
     * A captured title goes stale. The router's own title mixin rewrites the title on every
     * navigation, and a header re-created during a route change would capture the flashing title
     * itself and later "restore" it, leaving the tab reading Incoming for a call that ended long
     * ago. Reading the live title on every tick cannot get stuck that way.
     */
    startTitleFlash() {
      if (this.titleFlashIntervalId) {
        return
      }

      document.title = withMarker(document.title, TITLE_MARKERS.ringing)

      this.titleFlashIntervalId = setInterval(() => {
        document.title = markerOn(document.title) === TITLE_MARKERS.ringing
          ? withoutMarker(document.title)
          : withMarker(document.title, TITLE_MARKERS.ringing)
      }, 1000)
    },

    // The timer only. What the title should say instead is the caller's business - see
    // `syncTitleMarker`, which puts the steady marker on, and `clearTitleMarker`, which takes
    // everything off.
    stopTitleFlash() {
      if (this.titleFlashIntervalId) {
        clearInterval(this.titleFlashIntervalId)
        this.titleFlashIntervalId = null
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.softphone-header {
  position: relative;
  margin-right: 1rem;
}

.softphone-header__toggle {
  position: relative;
  padding: 0.25rem 0.5rem;
  color: var(--component-sidebar__toggle-font-color);
  text-decoration: none;

  &:hover,
  &:focus {
    color: var(--component-sidebar__toggle-font-color);
    text-decoration: none;
  }
}

/*
 * Says unclickable without reading as broken - offline is the widget's usual state. Aria-disabled
 * rather than disabled, so the button keeps the hover and the focus that carry its tooltip; the
 * click itself is refused in `toggle`.
 */
.softphone-header__toggle--idle {
  opacity: 0.75;
  cursor: default;
}

.softphone-header__icon {
  font-size: 1rem;
}

.softphone-header__label {
  font-size: 0.8rem;
  white-space: nowrap;
}

/* Matches the Support tab's unread badge - same corner, same size, same meaning. */
.softphone-header__alert {
  position: absolute;
  right: 2px;
  top: -2px;
  font-size: 7px;
  color: #fff;
  text-align: center;
  line-height: 14px;
  width: 15px;
  height: 14px;
  background: red;
  border-radius: 50%;
  animation: softphone-header-pulse 1s ease-in-out infinite;
}

.softphone-header__dot {
  position: absolute;
  right: 6px;
  top: 2px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
}

.softphone-header__dot--ready {
  background-color: #28a745;
}

.softphone-header__dot--connecting {
  background-color: #fd7e14;
}

.softphone-header__dot--active {
  background-color: #0d6efd;
}

.softphone-header__dot--wrap-up {
  background-color: #ffc107;
}

.softphone-header__dot--error {
  background-color: #dc3545;
}

/**
 * Anchored to the header rather than floated over the page: the softphone belongs to the chrome,
 * and an agent scrolling a campaigns list must not lose the call they are on.
 */
.softphone-header__panel {
  position: absolute;
  z-index: $zindex-dropdown;
  top: calc(100% + 0.5rem);
  right: 0;
  display: flex;
  flex-direction: column;
  width: 384px;
  padding: 1.25rem;
  border-radius: 0.375rem;
  /* The same surface the account switcher's menu uses, so the two read as one header. */
  background-color: $dropdown-bg;
  color: $dropdown-link-color;
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.3);
}

// Fixed under the header and no taller than the screen below it, with the rest scrolling.
@mixin softphone-panel-sheet {
  position: fixed;
  top: calc(#{$dashboard-header-height} + 0.25rem);
  right: 0.5rem;
  @include max-height-under-header(0.75rem);
  overflow-y: auto;
}

/*
 * Below md the header is a phone's width, and a 384px panel hung from the toggle's right edge
 * starts off the left of the screen, with Pick up and Decline in the part that is off it. There
 * the panel is a sheet across the screen under the header instead.
 */
@include media-breakpoint-down(sm) {
  .softphone-header {
    margin-right: 0.25rem;
  }

  .softphone-header__panel {
    @include softphone-panel-sheet;

    left: 0.5rem;
    width: auto;
  }
}

/*
 * A phone on its side is wider than md but shorter than the panel on a call, so the dropdown ran
 * off the bottom of the screen with Hang up and Mute in the part that was off it. There the panel
 * is a sheet at the right under the header, wide enough for the widget's two columns (see
 * SoftphoneWidget), which bring a call's panel down to the height of the screen. The sidebar is a
 * drawer there (see TheSidebar), so no column stands beside it.
 */
@include media-breakpoint-up(md) {
  @include short-screen {
    .softphone-header__panel {
      @include softphone-panel-sheet;

      width: 40rem;
      max-width: calc(100vw - 1rem);
    }
  }
}

@include short-screen {
  .softphone-header__panel {
    padding: 0.75rem 1rem;
  }
}

/*
 * The audio-devices menu hangs below its button over the panel. Where the panel scrolls it would cut
 * the menu off at its bottom edge, so it stops clipping while the menu is open. The menu is held to
 * the screen's height instead (see SoftphoneAudioDevices). Without :has() the menu is still there,
 * by scrolling the panel.
 */
.softphone-header__panel:has(.softphone-audio-devices__menu) {
  overflow: visible;
}

// The toggle is a 44px target wherever a finger is the pointer as a `.btn-icon`, and the widget and its
// audio menu size their own buttons.

@keyframes softphone-header-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.35; }
}
</style>
